import { createInstance, Identify } from '@amplitude/analytics-browser';
import type {
  BrowserClient,
  EnrichmentPlugin,
  ValidPropertyType,
} from '@amplitude/analytics-types';

import { AMPLITUDE_LOCALHOST_PROXY_URL } from './constants';

/**
 * A type representing the valid property types that can be set for an event or user.
 */
export type ValidProperties = Record<string, ValidPropertyType>;

/**
 * The Amplitude instance used for tracking events and user properties.
 */
let instance: BrowserClient | null = null;

/**
 * Indicates whether the Amplitude instance has already been initialized.
 */
let instanceInitialized = false;

/**
 * Properties set for the User and also tracked with each Event.
 */
let sharedProperties: ValidProperties = {};

/**
 * Properties tracked with each Event.
 */
let eventProperties: ValidProperties = {};

/**
 * Properties that are set when enriching the Amplitude events.
 * Note prefixing with 'amplitude' because this is very specific to Amplitude
 * event enrichment and if we move away from Amplitude we'll have to see how to
 * do this in the new anlytics library.
 */
let amplitudeEnrichmentProperties: ValidProperties = {};

/**
 * Returns the Amplitude instance if it exists, otherwise creates a new instance.
 * @returns The Amplitude instance
 */
const getAmplitudeInstance = () => {
  if (!instance) {
    instance = createInstance();
  }

  return instance;
};

/**
 * Enrichment plugin for adding shared properties to various events.
 * @returns The enrichment plugin for Amplitude
 * @see https://github.com/amplitude/Amplitude-TypeScript/blob/main/examples/plugins/page-view-tracking-enrichment/index.ts
 */
export function trackingEnrichmentPlugin(): EnrichmentPlugin {
  return {
    name: 'event-tracking-enrichment',
    type: 'enrichment',
    setup: async () => undefined,
    execute: async (event) => {
      if (
        event.event_type !== '[Amplitude] Page Viewed' &&
        event.event_type !== 'session_start' &&
        event.event_type !== 'session_end'
      ) {
        return event;
      }

      event.event_properties = {
        ...event.event_properties,
        ...sharedProperties,

        // Make sure the enrichment properties always override the shared properties.
        ...amplitudeEnrichmentProperties,
      };

      return event;
    },
  };
}

/**
 * Initializes the Amplitude analytics instance with the provided API key. Should only be used once.
 * @param apiKey The Amplitude API key to use for analytics
 */
export function initAnalytics(apiKey?: string) {
  if (!apiKey || instanceInitialized) {
    return;
  }

  // Adds plugins to the Amplitude instance
  getAmplitudeInstance().add(trackingEnrichmentPlugin());

  // See https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#autocapture for autocapture configuration options
  const origin = window.location.origin;

  getAmplitudeInstance().init(apiKey, {
    autocapture: {
      pageViews: true,
      sessions: true,
      formInteractions: true,
      fileDownloads: true,
    },
    serverUrl: origin.includes('localhost')
      ? AMPLITUDE_LOCALHOST_PROXY_URL
      : `${origin}/2/httpapi`,
  });

  instanceInitialized = true;
}

/**
 * Sets the user ID of the current user if it doesn't have one already.
 * @param userId
 */
const setUserId = (userId: string) => {
  if (!instanceInitialized) {
    return;
  }

  const currentUserId = getAmplitudeInstance().getUserId();

  if (!currentUserId) {
    getAmplitudeInstance().setUserId(userId);
  } else if (currentUserId !== userId) {
    console.warn(
      'User ID already set. Cannot set a different user ID after it has been set.',
    );
  }
};

/**
 * Dynamically sets user properties.
 * @param properties An object containing the properties to set for the current user
 */
export function setUserProperties(properties: ValidProperties = {}) {
  if (!instanceInitialized) {
    return;
  }

  if (Object.keys(properties).length > 0) {
    const identify = new Identify();

    for (const [key, val] of Object.entries(properties)) {
      if (key === 'user_id') {
        // Only attempt to set the user ID if it is truthy
        if (val) {
          setUserId(val as string);
        }

        continue;
      }

      identify.set(key, val);
    }

    // @TODO - WMG1-461 - Uncomment this when we're ready.
    // Clear any User level properties that we don't want to use anymore.
    // identify.unset('product');

    getAmplitudeInstance().identify(identify);
  }
}

/**
 * Sets properties tracked by each Event.
 * @param properties An object containing the properties
 */
export function setEventProperties(properties: ValidProperties) {
  if (!instanceInitialized) {
    return;
  }

  eventProperties = { ...eventProperties, ...properties };
}

/**
 * Sets properties to be shared between the Event and User properties.
 * @param properties An object containing the properties
 */
export function setSharedProperties(properties: ValidProperties) {
  if (!instanceInitialized) {
    return;
  }

  sharedProperties = { ...sharedProperties, ...properties };

  setUserProperties(properties);
}

/**
 * Sets properties used to enrich the Amplitude events.
 * @param properties An object containing the properties to set for the events.
 */
export function setAmplitudeEventEnrichmentProperties(
  properties: ValidProperties,
) {
  if (!instanceInitialized) {
    return;
  }

  amplitudeEnrichmentProperties = {
    ...amplitudeEnrichmentProperties,
    ...properties,
  };
}

/**
 * Function that opts the user out of analytics tracking.
 * @param optOut A boolean indicating whether to opt out of analytics tracking.
 */
export function setAnalyticsOptOut(optOut: boolean) {
  getAmplitudeInstance().setOptOut(optOut);
}

/**
 * Creates methods for tracking events and initializing analytics for an application.
 * @returns An object containing methods for tracking events and initializing analytics
 */
export function createAnalyticsModule() {
  let applicationName = 'default';

  /**
   * Initializes default event and user properties for an application.
   * @param application The name of the application, ie. 'Artist Roster'
   * @param defaultEventProperties Default event properties to set for events in this Application
   * @param defaultUserProperties Default user properties to set for the current user in this Application
   */
  const init = (
    application: string,
    defaultEventProperties: ValidProperties = {},
    defaultUserProperties: ValidProperties = {},
  ) => {
    applicationName = application;

    eventProperties = { ...eventProperties, ...defaultEventProperties };

    setUserProperties(defaultUserProperties);
  };

  /**
   * Tracks an event with the passed in event name and properties. Uses the shared
   * properties first, then the global event properties and finally the passed in
   * properties.
   * @param name The name of the event to track
   * @param properties The properties to set for the event
   */
  const trackEvent = (name: string, properties: ValidProperties = {}) => {
    if (!instanceInitialized) {
      return;
    }

    const allProperties: ValidProperties = {
      ...sharedProperties,
      ...eventProperties,
      application: applicationName,

      // NOTE: Allow the passed in properties to override the application name.
      //       Used in the Container (e.g. - useAnalytics('Artist Roster')).
      ...properties,
    };

    getAmplitudeInstance().track(name, allProperties);
  };

  // Expose flushing any batched events that haven't been sent yet.
  // https://amplitude.com/docs/sdks/analytics/browser/browser-sdk-2#configure-batching-behavior
  const flushAnalytics = () => {
    getAmplitudeInstance().flush();
  };

  return { init, trackEvent, flushAnalytics };
}

/**
 * Function that checks if the Amplitude instance has been initialized.
 * @returns A boolean indicating whether the Amplitude instance has been initialized.
 */
export function isAnalyticsInitialized() {
  return instanceInitialized;
}
